import React, { useState } from 'react';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';

const AccommodationGallery = ({ images = [], accommodationType = "Deluxe Forest Villa" }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const defaultImages = [
    {
      id: 1,
      src: "https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg",
      alt: "Deluxe Forest Villa - Main View",
      caption: "Spacious villa with forest views"
    },
    {
      id: 2,
      src: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
      alt: "Deluxe Forest Villa - Bedroom",
      caption: "Comfortable king-size bed"
    },
    {
      id: 3,
      src: "https://images.pexels.com/photos/1571468/pexels-photo-1571468.jpeg",
      alt: "Deluxe Forest Villa - Bathroom",
      caption: "Modern bathroom with premium amenities"
    },
    {
      id: 4,
      src: "https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg",
      alt: "Deluxe Forest Villa - Living Area",
      caption: "Cozy living space with nature views"
    },
    {
      id: 5,
      src: "https://images.pexels.com/photos/271618/pexels-photo-271618.jpeg",
      alt: "Deluxe Forest Villa - Balcony",
      caption: "Private balcony overlooking the forest"
    },
    {
      id: 6,
      src: "https://images.pexels.com/photos/1571467/pexels-photo-1571467.jpeg",
      alt: "Deluxe Forest Villa - Dining",
      caption: "In-room dining area"
    }
  ];

  const galleryImages = images?.length > 0 ? images : defaultImages;

  const openLightbox = (index) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % galleryImages?.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + galleryImages?.length) % galleryImages?.length);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape') closeLightbox();
    if (e?.key === 'ArrowRight') nextImage();
    if (e?.key === 'ArrowLeft') prevImage();
  };

  return (
    <div className="w-full">
      {/* Main Gallery Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
        {/* Main Image */}
        <div 
          className="col-span-2 row-span-2 relative group cursor-pointer overflow-hidden rounded-lg"
          onClick={() => openLightbox(0)}
        >
          <Image
            src={galleryImages?.[0]?.src}
            alt={galleryImages?.[0]?.alt}
            className="w-full h-64 md:h-96 object-cover transition-organic group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-organic flex items-center justify-center">
            <div className="bg-white/90 rounded-full p-3">
              <Icon name="ZoomIn" size={24} className="text-primary" />
            </div>
          </div>
        </div>

        {/* Thumbnail Images */}
        {galleryImages?.slice(1, 5)?.map((image, index) => (
          <div
            key={image?.id}
            className="relative group cursor-pointer overflow-hidden rounded-lg"
            onClick={() => openLightbox(index + 1)}
          >
            <Image
              src={image?.src}
              alt={image?.alt}
              className="w-full h-20 md:h-32 object-cover transition-organic group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-organic flex items-center justify-center">
              <div className="bg-white/90 rounded-full p-2">
                <Icon name="ZoomIn" size={16} className="text-primary" />
              </div>
            </div>
          </div>
        ))}

        {/* View All Button */}
        {galleryImages?.length > 5 && (
          <div
            className="relative group cursor-pointer overflow-hidden rounded-lg bg-muted flex items-center justify-center"
            onClick={() => openLightbox(5)}
          >
            <div className="text-center">
              <Icon name="Grid3X3" size={24} className="text-primary mx-auto mb-1" />
              <p className="text-xs font-medium text-primary">
                +{galleryImages?.length - 5} more
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <div className="relative max-w-4xl max-h-full" onClick={(e) => e?.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white hover:text-accent transition-micro z-10"
            >
              <Icon name="X" size={32} />
            </button>

            {/* Navigation Buttons */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 rounded-full p-3 text-white transition-micro z-10"
            >
              <Icon name="ChevronLeft" size={24} />
            </button>

            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 rounded-full p-3 text-white transition-micro z-10"
            >
              <Icon name="ChevronRight" size={24} />
            </button>

            {/* Main Image */}
            <div className="relative">
              <Image
                src={galleryImages?.[selectedImage]?.src}
                alt={galleryImages?.[selectedImage]?.alt}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              
              {/* Image Caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6 rounded-b-lg">
                <p className="text-white text-lg font-medium">
                  {galleryImages?.[selectedImage]?.caption}
                </p>
                <p className="text-white/80 text-sm">
                  {selectedImage + 1} of {galleryImages?.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccommodationGallery;